import { useEffect, useState } from "react";
import { Zap } from "lucide-react";
import { useAiCreditWallet, formatRefillCountdown } from "@/hooks/useAiCredits";
import { AiCreditWalletDialog } from "./AiCreditWalletDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export function AiCreditBadge({ workspaceId }: { workspaceId: string }) {
  const { data: wallet, isLoading } = useAiCreditWallet(workspaceId);
  const balance = wallet?.total ?? 0;
  const [open, setOpen] = useState(false);
  const empty = !isLoading && balance === 0;
  const [, force] = useState(0);
  useEffect(() => {
    if (!empty) return;
    const t = setInterval(() => force((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, [empty]);
  const refillIn = wallet ? formatRefillCountdown(wallet.nextRefillAt) : "";

  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className={cn(
                "inline-flex items-center gap-1.5 h-9 px-3 rounded-full border transition-colors",
                "text-sm font-semibold tabular-nums",
                empty
                  ? "bg-destructive/10 border-destructive/20 text-destructive hover:bg-destructive/15"
                  : "bg-primary/10 border-primary/20 text-primary hover:bg-primary/15"
              )}
            >
              <Zap className={cn("h-3.5 w-3.5 fill-current", empty && "animate-pulse")} />
              <span>{isLoading ? "…" : empty ? `Refills in ${refillIn}` : `${balance} credits`}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            5 free credits/day (no rollover) · Paid credits never expire
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <AiCreditWalletDialog open={open} onOpenChange={setOpen} workspaceId={workspaceId} />
    </>
  );
}
